import React, { useEffect, useRef } from 'react';

import 'components/GMetrixContentTreeview/GMetrixContentTreeview.scss';

type TreeNode = {
    [key: string]: any;
    items?: TreeNode[];
    key: string;
    text: string;
};

type GMetrixContentTreeviewProps = {
    checkboxes?: boolean;
    checkedIds?: string[];
    className?: string;
    data: TreeNode[];
    expandedIds?: string[];
    flatList?: any[];
    onCheckChange?: (newCheckedIds: string[], newSelected: any[]) => void;
    onExpandChange?: (newExpandedIds: string[]) => void;
    renderContent?: (item: any, level: number) => React.ReactNode;
    renderDeliverableInfo?: (item: any) => React.ReactNode;
    renderIcon?: (item: any) => React.ReactNode;
};

const getAllLeafKeys = (node: TreeNode): string[] => {

    const keys: string[] = [];

    if (!node.items || node.items.length === 0) {

        keys.push(node.key);

    } else {

        for (let i = 0; i < node.items.length; i++) {

            keys.push(...getAllLeafKeys(node.items[i]));

        }

    }
    return keys;

};

const getCheckState = (node: TreeNode, checkedSet: Set<string>): 'checked' | 'unchecked' | 'indeterminate' => {

    if (!node.items || node.items.length === 0) {

        return checkedSet.has(node.key) ? 'checked' : 'unchecked';

    }

    let allChecked = true;
    let anyChecked = false;

    for (let i = 0; i < node.items.length; i++) {

        const childState = getCheckState(node.items[i], checkedSet);

        if (childState === 'checked') {

            anyChecked = true;

        } else if (childState === 'indeterminate') {

            anyChecked = true;
            allChecked = false;

        } else {

            allChecked = false;

        }

    }

    return allChecked && anyChecked ? 'checked' : anyChecked ? 'indeterminate' : 'unchecked';

};

const TreeCheckbox = ({ checkedSet, node, onCheck }: { checkedSet: Set<string>; node: TreeNode; onCheck: (node: TreeNode) => void }) => {

    const state = getCheckState(node, checkedSet);
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {

        if (ref.current) {

            ref.current.indeterminate = state === 'indeterminate';

        }

    }, [state]);

    return (
        <input
            checked={state === 'checked'}
            className="gmetrix-treeview__checkbox"
            onChange={() => onCheck(node)}
            onClick={e => e.stopPropagation()}
            ref={ref}
            type="checkbox"
        />
    );

};

export const GMetrixContentTreeview = (props: GMetrixContentTreeviewProps) => {

    const {
        checkboxes = true,
        checkedIds = [],
        className = '',
        data,
        expandedIds = [],
        flatList = [],
        onCheckChange,
        onExpandChange,
        renderContent,
        renderIcon
    } = props;

    const checkedSet = new Set(checkedIds);

    const toggleExpand = (key: string) => {

        const newExpandedIds = expandedIds.slice();
        const index = newExpandedIds.indexOf(key);

        if (index === -1) {

            newExpandedIds.push(key);

        } else {

            newExpandedIds.splice(index, 1);

        }

        onExpandChange?.(newExpandedIds);

    };

    const handleCheck = (node: TreeNode) => {

        const currentState = getCheckState(node, checkedSet);
        const shouldCheck = currentState !== 'checked';
        const newCheckedSet = new Set(checkedIds);

        // Get all leaf keys under this node
        const leafKeys = getAllLeafKeys(node);

        if (shouldCheck) {

            // Add all leaf descendants
            for (let i = 0; i < leafKeys.length; i++) {

                newCheckedSet.add(leafKeys[i]);

            }

        } else {

            // Remove all leaf descendants
            for (let i = 0; i < leafKeys.length; i++) {

                newCheckedSet.delete(leafKeys[i]);

            }

        }

        const newCheckedArray = Array.from(newCheckedSet);

        // Build selected deliverables from flat list
        const newSelected: any[] = [];

        for (let i = 0; i < newCheckedArray.length; i++) {

            const found = flatList.find((d: any) => d.key === newCheckedArray[i]);

            if (found) {

                const alreadyExists = newSelected.find(
                    (s: any) => s.Id === found.Id && s.DeliverableTypeId === found.DeliverableTypeId && s.ProductId === found.ProductId
                );

                if (!alreadyExists) {

                    newSelected.push(found);

                }

            }

        }

        onCheckChange?.(newCheckedArray, newSelected);

    };

    const getNodeLevel = (key: string): number => key.split('_').length;

    const renderNode = (node: TreeNode, depth: number): React.ReactNode => {

        const hasChildren = node.items && node.items.length > 0;
        const isExpanded = expandedIds.includes(node.key);
        const level = getNodeLevel(node.key);
        const isLeaf = !hasChildren;
        const isContentItem = isLeaf && node.Id;

        // Determine node class based on level
        let nodeClass = '';

        if (level === 1) {

            nodeClass = 'gmetrix-treeview__category-node';

        } else if (level === 2) {

            nodeClass = 'gmetrix-treeview__product-node';

        } else if (level === 3) {

            nodeClass = 'gmetrix-treeview__deliverable-type-node';

        } else if (isContentItem) {

            nodeClass = 'gmetrix-treeview__content-item-node';

        }

        const isDeliverableTypeLevel = level === 3 && hasChildren;

        return (
            <li className={`gmetrix-treeview__node-item ${nodeClass}`} key={node.key}>
                <div
                    className="gmetrix-treeview__node-row"
                    onClick={() => hasChildren ? toggleExpand(node.key) : undefined}
                >
                    <span
                        className={`gmetrix-treeview__expand-icon ${isExpanded ? 'gmetrix-treeview__expand-icon--expanded' : ''} ${!hasChildren ? 'gmetrix-treeview__expand-icon--hidden' : ''}`}
                    >
                        <i className="fa-solid fa-caret-right"></i>
                    </span>
                    {checkboxes ? (
                        <TreeCheckbox
                            checkedSet={checkedSet}
                            node={node}
                            onCheck={handleCheck}
                        />
                    ) : null}
                    <span className="gmetrix-treeview__node-content">
                        {isContentItem && renderContent ? (
                            renderContent(node, level)
                        ) : (
                            <div className="d-flex align-items-center">
                                {level >= 3 && renderIcon ? renderIcon(node) : null}
                                <span>
                                    {node.text}
                                </span>
                            </div>
                        )}
                    </span>
                </div>
                {hasChildren && isExpanded ? (
                    <div className={`gmetrix-treeview__children ${isDeliverableTypeLevel ? 'gmetrix-treeview__deliverable-bg' : ''}`}>
                        <ul className="gmetrix-treeview__node-list">
                            {(node.items || []).map(child => renderNode(child, depth + 1))}
                        </ul>
                    </div>
                ) : null}
            </li>
        );

    };

    return (
        <div className={`gmetrix-treeview ${className}`}>
            <ul className="gmetrix-treeview__node-list">
                {data.map(node => renderNode(node, 0))}
            </ul>
        </div>
    );

};
